/**
 * Token canonicalization — the stage that turns surface words into vocabulary
 * terms (plan §3, M3).
 *
 * @remarks
 * Runs per clause, after tokenization, in exactly this order:
 *
 *   1. Stopword stripping (built-in articles/filler + authored extensions).
 *   2. Greedy multiword longest-match (`pick up` → `take`) over the FILTERED
 *      stream — first-word buckets arrive pre-sorted longest-first from
 *      compilation, so the first sequence match is the longest. Matching over
 *      the filtered stream is deliberate: stopwords are stripped first (plan
 *      §3's stated order), and compilation rejects aliases containing
 *      stopword/reserved components, so no legal alias can be broken by it.
 *   3. Single-word classification: pronoun → intent alias → direction alias →
 *      plain word.
 *
 * Prepositions are deliberately NOT classified here — they are a positional
 * namespace (M1 discovery 3): `in` is only a preposition after a verb that
 * declares it, which is the structure matcher's call. Every canonical token
 * keeps its source span and surface text so corrections and echoes can point
 * at exactly what the player typed, and `wasAliasMatched` records
 * non-canonical sources for confidence composition (plan §6.1, applied in M4 —
 * NB: only intent and noun aliases weigh in; direction aliases are echo-only).
 *
 * @packageDocumentation
 */

import {
	BUILTIN_ARTICLES,
	BUILTIN_PRONOUN_TOKENS,
	type PronounWord,
} from '../lexicon/builtin-function-words.js';
import { type CompiledIntent, type MultiwordAlias } from '../lexicon/compile-lexicon.js';
import { proposeVerbBaseForms } from '../lexicon/match-verb-morphology.js';
import { type DirectionToken, type SourceSpan, type Token } from '../types.js';
import { mergeTwoWordPrepositions } from './two-word-prepositions.js';

/** The classified form of one or more clause tokens after canonicalization. */
export type CanonicalToken =
	| DirectionCanonicalToken
	| IntentCanonicalToken
	| PronounCanonicalToken
	| WordCanonicalToken;

/** Fields every canonical token carries: where it came from and what the player actually typed. */
export interface CanonicalTokenBase {
	/** Half-open range into the folded clause text (spans the full alias for multiword matches). */
	readonly sourceSpan: SourceSpan;
	/** The folded surface form as typed (`pick up`, `n`) — echoes and positional preposition checks read this. */
	readonly sourceText: string;
}

/**
 * The narrow vocabulary port canonicalization reads — `CompiledLexicon`
 * satisfies it structurally.
 */
export interface CanonicalizationVocabulary {
	readonly directionsByToken: ReadonlyMap<string, DirectionToken>;
	readonly intentsByToken: ReadonlyMap<string, CompiledIntent>;
	readonly multiwordAliasesByFirstToken: ReadonlyMap<string, readonly MultiwordAlias[]>;
	readonly stopwordSet: ReadonlySet<string>;
}

/** A token recognized as a direction (canonical or alias). */
export interface DirectionCanonicalToken extends CanonicalTokenBase {
	readonly direction: DirectionToken;
	readonly kind: 'direction';
	/**
	 * True when the surface form differed from the canonical token (`n` →
	 * `north`) — surfaced for UI echoes only. Direction aliases carry NO
	 * confidence penalty: plan §6.1 weighs alias-matched intents and nouns,
	 * and authored shorthand expresses equivalence, not doubt.
	 */
	readonly wasAliasMatched: boolean;
}

/** A token (or multiword run) recognized as an intent. */
export interface IntentCanonicalToken extends CanonicalTokenBase {
	readonly intent: CompiledIntent;
	readonly kind: 'intent';
	/** True when the surface form differed from the canonical token (`grab`, `pick up` → `take`). */
	readonly wasAliasMatched: boolean;
}

/** A built-in pronoun token (`it`, `them`), resolved against recency in M6. */
export interface PronounCanonicalToken extends CanonicalTokenBase {
	readonly kind: 'pronoun';
	readonly pronoun: PronounWord;
}

/** An unclassified word — noun-phrase material and would-be prepositions, judged by the structure matcher. */
export interface WordCanonicalToken extends CanonicalTokenBase {
	/**
	 * D242: a declared preposition the catalog's own name holds (`coin FROM
	 * home`) — phrase material, never a binding (`prepositions-inside-names.ts`).
	 * Present only when true.
	 */
	readonly isNameMaterial?: true;
	/**
	 * D212: the word directly followed an article (D60's trace), so a noun
	 * phrase STARTS here — what lets the grammar read `give sylvain the
	 * compass` as two phrases. Present only when true.
	 */
	readonly isNounPhraseStart?: true;
	readonly kind: 'word';
}

/**
 * Canonicalize one clause's tokens against the compiled vocabulary.
 *
 * @param tokens - A clause's tokens as produced by the tokenizer/splitters
 * @param vocabulary - The compiled vocabulary view (see {@link CanonicalizationVocabulary})
 * @returns Canonical tokens in clause order; empty when everything was a stopword
 */
export const canonicalizeTokens = (
	tokens: readonly Token[],
	vocabulary: CanonicalizationVocabulary,
): readonly CanonicalToken[] => {
	const canonicalTokens = canonicalizeWithStopwords(tokens, vocabulary, vocabulary.stopwordSet);

	/*
	 * D212: the verb in play is the authority on its own words (D56's rule for
	 * particles, applied to prepositions). A pack may list `to` as a stopword
	 * — it is why `talk to sylvain` reads as `talk sylvain` — and for most
	 * verbs that is right. But an intent that DECLARES a binding on a stopword
	 * (`give … to <recipient>`) has said the word means something after it, so
	 * for that clause the word is not noise: the clause is read again with
	 * the head intent's bound prepositions kept. Only the head decides;
	 * `talk to sylvain` still strips, and a bound `to` under `give` never
	 * changes what `talk` sees.
	 */
	const [head] = canonicalTokens;

	if (head?.kind !== 'intent') {
		return canonicalTokens;
	}

	// D242: a two-word binding's words are the verb's too (`up to`), and the pair is read as one word.
	const boundStopwords = [...head.intent.prepositionRoles.keys()].flatMap((binding) => binding.split(' ')).filter((word) => vocabulary.stopwordSet.has(word));

	if (boundStopwords.length === 0) {
		return mergeTwoWordPrepositions(canonicalTokens);
	}

	const stopwordsForThisVerb = new Set([...vocabulary.stopwordSet].filter((word) => !boundStopwords.includes(word)));

	return mergeTwoWordPrepositions(canonicalizeWithStopwords(tokens, vocabulary, stopwordsForThisVerb));
};

/**
 * The canonicalization walk under one stopword set — the whole of
 * {@link canonicalizeTokens} before D212 let the head verb keep a stopword it
 * binds.
 *
 * @param tokens - A clause's tokens
 * @param vocabulary - The compiled vocabulary view
 * @param stopwordSet - The words to strip before anything is classified
 * @returns Canonical tokens in clause order
 */
const canonicalizeWithStopwords = (
	tokens: readonly Token[],
	vocabulary: CanonicalizationVocabulary,
	stopwordSet: ReadonlySet<string>,
): readonly CanonicalToken[] => {
	/*
	 * D60: articles are stripped, but not before leaving their one grammatical
	 * trace — a word directly after `a` / `an` / `some` / `the` is noun-phrase
	 * material, and English does not put a verb there. The set survives only for
	 * this pass; the grammar never sees the articles themselves.
	 */
	const nounPhraseStarts = new Set(
		tokens.flatMap((token, tokenIndex) => {
			const nextToken = tokens[tokenIndex + 1];

			return (BUILTIN_ARTICLES as readonly string[]).includes(token.text) && nextToken !== undefined ? [nextToken] : [];
		}),
	);
	const contentTokens = tokens.filter((token) => !stopwordSet.has(token.text));

	// D59: the SHIFTED phrasal reading is tried on the whole clause before the
	// per-token walk, because it is a claim about the clause's two ENDS at once
	// — something no left-to-right greedy pass can see.
	const shiftedPhrasal = matchShiftedPhrasalAlias(contentTokens, vocabulary);

	if (shiftedPhrasal !== undefined) {
		return shiftedPhrasal;
	}

	const canonicalTokens: CanonicalToken[] = [];

	// Tokens consumed by a multiword match are skipped by count rather than by
	// re-indexing — `entries()` hands us each token already narrowed, so no
	// undefined-guard ever needs to exist here (coverage ruling, 2026-08-12).
	let skipTokenCount = 0;

	for (const [tokenIndex, currentToken] of contentTokens.entries()) {
		if (skipTokenCount > 0) {
			skipTokenCount -= 1;
			continue;
		}

		const multiwordMatch = matchMultiwordAlias(currentToken, contentTokens, tokenIndex, vocabulary);

		if (multiwordMatch !== undefined) {
			canonicalTokens.push(multiwordMatch.canonicalToken);
			skipTokenCount = multiwordMatch.consumedTokenCount - 1;
			continue;
		}

		canonicalTokens.push(classifySingleToken(currentToken, vocabulary, nounPhraseStarts.has(currentToken)));
	}

	return canonicalTokens;
};

/**
 * The SHIFTED reading of a two-word phrasal alias (D59): `w1 <phrase> w2`
 * canonicalized as if the player had typed `w1 w2 <phrase>`.
 *
 * @remarks
 * English separable phrasals shift their second word past the object — "pick
 * the bar up", "put it down", "pry the locker open" — and with a pronoun the
 * shift is mandatory. The ADJACENT form has matched here since M2 via the
 * multiword-alias walk; this is its mirror, and it is what makes a cross-verb
 * phrasal (an alias whose first word is not itself a verb the vocabulary
 * declares, or is a different verb entirely) work in both positions. Same-verb
 * phrasals ("turn the kettle on") never reach this: their first word IS the
 * intent and their second is a declared PARTICLE, bound by the grammar walk
 * (D56).
 *
 * The claim is deliberately narrow, because every widening is a false-positive
 * risk on ordinary noun phrases:
 *
 * - exactly TWO-word aliases (a three-word alias cannot shift meaningfully);
 * - `w1` must open the clause (a verb leads its clause) and must NOT read as a
 *   phrasal any other way from position 0 — the greedy adjacent match would
 *   already have it;
 * - `w2` must CLOSE the clause, with at least one token between (the adjacent
 *   form is the other matcher's);
 * - intent targets only — a shifted direction alias is not a thing English has.
 *
 * Where `w1` is ALSO a declared intent (the POC declares `pry`, so `pry the
 * locker open` would otherwise read as intent `pry` + a second verb `open` and
 * die in demotion with a spurious clarification), the shifted phrasal outranks
 * the single-verb reading: the player named the phrasal's action, and the
 * phrasal's canonical verb is what they meant.
 *
 * @param contentTokens - The stopword-filtered token stream
 * @param vocabulary - The compiled vocabulary view
 * @returns The full canonicalized clause, or `undefined` when this is not a shifted phrasal
 */
const matchShiftedPhrasalAlias = (
	contentTokens: readonly Token[],
	vocabulary: CanonicalizationVocabulary,
): undefined | readonly CanonicalToken[] => {
	const firstToken = contentTokens[0];
	const lastToken = contentTokens.at(-1);

	if (firstToken === undefined || lastToken === undefined || contentTokens.length < 3) {
		return undefined;
	}

	const shiftedAlias = vocabulary.multiwordAliasesByFirstToken
		.get(firstToken.text)
		?.find(
			(candidate) =>
				candidate.target.kind === 'intent' &&
				candidate.tokenSequence.length === 2 &&
				candidate.tokenSequence[1] === lastToken.text,
		);

	if (shiftedAlias === undefined || shiftedAlias.target.kind !== 'intent') {
		return undefined;
	}

	const intentToken: CanonicalToken = {
		intent: resolveIntentRecord(shiftedAlias.target.token, vocabulary),
		kind: 'intent',
		// The span covers the whole discontinuous phrasal — refusals quote what
		// the player MEANT as the verb, which is both its words.
		sourceSpan: { end: lastToken.sourceSpan.end, start: firstToken.sourceSpan.start },
		sourceText: `${firstToken.text} ${lastToken.text}`,
		wasAliasMatched: true,
	};

	return [
		intentToken,
		...contentTokens.slice(1, -1).map((middleToken) => classifySingleToken(middleToken, vocabulary)),
	];
};

/** A successful greedy multiword match and how many source tokens it consumed. */
interface MultiwordMatch {
	readonly canonicalToken: CanonicalToken;
	readonly consumedTokenCount: number;
}

/**
 * Classify one single token: pronoun, intent, direction, or plain word.
 *
 * @param token - The source token
 * @param vocabulary - The compiled vocabulary view
 * @returns The canonical token
 */
const classifySingleToken = (
	token: Token,
	vocabulary: CanonicalizationVocabulary,
	isNounPhraseStart: boolean = false,
): CanonicalToken => {
	if ((BUILTIN_PRONOUN_TOKENS as readonly string[]).includes(token.text)) {
		return {
			kind: 'pronoun',
			pronoun: token.text as PronounWord,
			sourceSpan: token.sourceSpan,
			sourceText: token.text,
		};
	}

	/*
	 * D60 — the determiner rule, and the fix D54 recorded as debt. "take the
	 * pry bar" made `pry` an intent token the moment D49 declared the verb, so
	 * the clause died as second_verb, the demotion reparse found no bar in the
	 * room, and the catalog-is-judge rule threw away the useful teach — the
	 * player read `TAKE could not carry "pry" anywhere useful` for a two-word
	 * noun they had just seen the game print.
	 *
	 * English already answers this: a word directly after an article is inside
	 * a noun phrase, and no verb lives there. Classifying it as a plain word
	 * UP FRONT means the second-verb collision never happens and the ordinary
	 * entity path speaks — the entity miss (`You see no pry bar here` since D213) — with no
	 * rescue, no demotion, and no discarded diagnostics.
	 *
	 * The D21 corpus pins survive untouched, because neither has an article:
	 * "take take the keycard" collides verb-on-verb, and "take pry" keeps its
	 * refused-rescue teach. Directions need no such rule — mid-phrase
	 * directions already demote to words in the grammar walk.
	 */
	const matchedIntent = isNounPhraseStart ? undefined : vocabulary.intentsByToken.get(token.text);

	if (matchedIntent !== undefined) {
		return {
			intent: matchedIntent,
			kind: 'intent',
			sourceSpan: token.sourceSpan,
			sourceText: token.text,
			wasAliasMatched: matchedIntent.canonicalToken !== token.text,
		};
	}

	// D36: regular English inflection reaches the base form — "kicking",
	// "smashed", "opens" find their verb without any alias enumerating them.
	// Tried only after exact/alias matching fails, and only a form the
	// vocabulary ALREADY knows is accepted, so nothing is invented. The D60
	// determiner rule gates this too: "the opening" is a noun.
	for (const baseForm of isNounPhraseStart ? [] : proposeVerbBaseForms(token.text)) {
		const inflectedIntent = vocabulary.intentsByToken.get(baseForm);

		if (inflectedIntent !== undefined) {
			return {
				intent: inflectedIntent,
				kind: 'intent',
				sourceSpan: token.sourceSpan,
				sourceText: token.text,
				wasAliasMatched: true,
			};
		}
	}

	const matchedDirection = vocabulary.directionsByToken.get(token.text);

	if (matchedDirection !== undefined) {
		return {
			direction: matchedDirection,
			kind: 'direction',
			sourceSpan: token.sourceSpan,
			sourceText: token.text,
			wasAliasMatched: matchedDirection !== token.text,
		};
	}

	return { ...(isNounPhraseStart ? { isNounPhraseStart: true as const } : {}), kind: 'word', sourceSpan: token.sourceSpan, sourceText: token.text };
};

/**
 * Try a greedy multiword alias match starting at one position. Buckets are
 * pre-sorted longest-first, so the first hit is the longest possible.
 *
 * @param firstToken - The already-narrowed token at `startIndex` (the caller's loop proved it exists)
 * @param contentTokens - The stopword-filtered token stream
 * @param startIndex - Where the candidate run begins
 * @param vocabulary - The compiled vocabulary view
 * @returns The match, or undefined when no alias sequence fits here
 */
const matchMultiwordAlias = (
	firstToken: Token,
	contentTokens: readonly Token[],
	startIndex: number,
	vocabulary: CanonicalizationVocabulary,
): MultiwordMatch | undefined => {
	const aliasBucket = vocabulary.multiwordAliasesByFirstToken.get(firstToken.text);

	if (aliasBucket === undefined) {
		return undefined;
	}

	for (const multiwordAlias of aliasBucket) {
		const matchedRun = takeMatchingRun(contentTokens, firstToken, startIndex, multiwordAlias.tokenSequence);

		if (matchedRun === undefined) {
			continue;
		}

		const sourceSpan: SourceSpan = { end: matchedRun.lastConsumedToken.sourceSpan.end, start: firstToken.sourceSpan.start };
		const base = { sourceSpan, sourceText: matchedRun.sourceText, wasAliasMatched: true } as const;

		return {
			canonicalToken:
				multiwordAlias.target.kind === 'intent'
					? { ...base, intent: resolveIntentRecord(multiwordAlias.target.token, vocabulary), kind: 'intent' }
					: { ...base, direction: multiwordAlias.target.token, kind: 'direction' },
			consumedTokenCount: matchedRun.consumedTokenCount,
		};
	}

	return undefined;
};

/**
 * Look up the compiled record behind a multiword alias's canonical intent
 * token. The record always exists — compilation registered it — so a miss is a
 * programmer error and throws (architecture.md §3.3).
 *
 * @param canonicalToken - The alias target's canonical intent token
 * @param vocabulary - The compiled vocabulary view
 * @returns The compiled intent record
 * @throws Error if the record is missing (corrupt vocabulary — cannot happen via compileLexicon)
 */
const resolveIntentRecord = (canonicalToken: string, vocabulary: CanonicalizationVocabulary): CompiledIntent => {
	const intentRecord = vocabulary.intentsByToken.get(canonicalToken);

	if (intentRecord === undefined) {
		throw new Error(`corrupt vocabulary: multiword alias targets unregistered intent "${canonicalToken}"`);
	}

	return intentRecord;
};

/** A successful alias-sequence match: how far it reached and what it read. */
interface MatchedAliasRun {
	readonly consumedTokenCount: number;
	/** The run's final token — its span end closes the whole match's span. */
	readonly lastConsumedToken: Token;
	/** The consumed surface texts, space-joined (`pick up`). */
	readonly sourceText: string;
}

/**
 * Check whether an alias token sequence matches the stream at a position,
 * returning the run's summary when it does.
 *
 * @param contentTokens - The stopword-filtered token stream
 * @param firstToken - The already-narrowed token at `startIndex`
 * @param startIndex - Where the candidate run begins
 * @param tokenSequence - The alias's expected token texts
 * @returns The matched run, or undefined on any mismatch (including running off the stream's end)
 */
const takeMatchingRun = (
	contentTokens: readonly Token[],
	firstToken: Token,
	startIndex: number,
	tokenSequence: readonly string[],
): MatchedAliasRun | undefined => {
	const consumedTexts: string[] = [];

	let lastConsumedToken = firstToken;

	for (const [sequenceIndex, expectedText] of tokenSequence.entries()) {
		const candidateToken = sequenceIndex === 0 ? firstToken : contentTokens[startIndex + sequenceIndex];

		if (candidateToken === undefined || candidateToken.text !== expectedText) {
			return undefined;
		}

		consumedTexts.push(candidateToken.text);
		lastConsumedToken = candidateToken;
	}

	return {
		consumedTokenCount: tokenSequence.length,
		lastConsumedToken,
		sourceText: consumedTexts.join(' '),
	};
};
